import { useState, type ReactNode } from 'react'
import { BarChart3, ChevronRight, Clock, Settings } from 'lucide-react'
import {
  SidebarMenuButton,
  SidebarMenuSkeleton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@teach/ui'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { MarqueeTitle } from './MarqueeTitle'

const RecentAction = ({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: ReactNode
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
  >
    {children}
  </button>
)

interface Props {
  /** `null` — ещё грузится: скелетон, а не «Нет курсов». */
  recentCourses: { id: number; title: string }[] | null
  isCourseActive: (courseId: number) => boolean
  onNavigate: (path: string) => void
}

export const RecentCoursesMenu = ({ recentCourses, isCourseActive, onNavigate }: Props) => {
  const [recentOpen, setRecentOpen] = useState(true)
  const m = useMessages(messages)

  return (
    <SidebarMenuItem>
      <SidebarMenuButton onClick={() => setRecentOpen((o) => !o)}>
        <Clock className="size-[17px]" />
        <span>{m.recent}</span>
        <ChevronRight
          className={`ml-auto size-[17px] transition-transform duration-200 ${recentOpen ? 'rotate-90' : ''}`}
        />
      </SidebarMenuButton>
      {recentOpen ? (
        // Правый край — вровень с основными строками меню («Мои курсы»): у
        // вложенного списка по умолчанию справа отступы, и строки были короче.
        <SidebarMenuSub className="mr-0 pr-0">
          {recentCourses === null ? (
            <SidebarMenuSubItem aria-hidden="true">
              <SidebarMenuSkeleton className="px-2" />
              <SidebarMenuSkeleton className="px-2" />
            </SidebarMenuSubItem>
          ) : recentCourses.length > 0 ? (
            recentCourses.map((course) => (
              <SidebarMenuSubItem key={course.id} className="group/recent relative">
                <SidebarMenuSubButton
                  isActive={isCourseActive(course.id)}
                  onClick={() => onNavigate(`/editor/${course.id}`)}
                  className="truncate cursor-pointer group-hover/recent:pr-16"
                >
                  <MarqueeTitle title={course.title} />
                </SidebarMenuSubButton>
                {/* Быстрые действия по наведению: статистика и настройки (редактор — клик по строке). */}
                <div className="absolute right-1 top-1/2 flex -translate-y-1/2 gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/recent:opacity-100">
                  <RecentAction
                    label={m.courseStats}
                    onClick={() => onNavigate(`/stats/${course.id}`)}
                  >
                    <BarChart3 className="size-3.5" />
                  </RecentAction>
                  <RecentAction
                    label={m.courseSettings}
                    onClick={() => onNavigate(`/course-settings/${course.id}`)}
                  >
                    <Settings className="size-3.5" />
                  </RecentAction>
                </div>
              </SidebarMenuSubItem>
            ))
          ) : (
            <SidebarMenuSubItem>
              <SidebarMenuSubButton asChild>
                <span className="text-muted-foreground">{m.noCourses}</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          )}
        </SidebarMenuSub>
      ) : null}
    </SidebarMenuItem>
  )
}
